import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Calendar,
  User,
  Building2,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Save,
  X,
  Mail,
  Loader2,
  Plus,
  FolderOpen,
  Package,
  RefreshCw,
  Printer,
  Download,
  CalendarDays,
  Gavel,
  UserCheck,
  XCircle,
  Users
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { JudgeOptionLabel } from "../components/JudgeOptionLabel";
import { JudgeExclusionsCard } from "../components/JudgeExclusionsCard";
import { ActNumberButton, ActNumberDeleteButton } from "../components/ActNumberButton";
import { downloadReassignmentAct, downloadActFromUrl } from "../components/actDownloads";
import { ActIssueDialog } from "../components/ActIssueDialog";
import { SolonCheckButton } from "../components/SolonCheckButton";
import { WithdrawalFields, Settlement214aField, MediationFields } from "../components/CaseExtraFields";
import { SolonTabsView } from "../components/SolonTabsView";
import { JudgeAssignDialog, CompositionAssignDialog } from "../components/CaseActDialogs";
import { DateInput } from "../components/ui/date-input";

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const privacy = user?.privacy_mode || false;
  const p = (text) => privacy ? anonymize(text) : text;
  const courtBranch = user?.court_branch || "Πρωτοδικείο Αθηνών";
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [pretrialJudges, setPretrialJudges] = useState([]);
  const [showJudgePicker, setShowJudgePicker] = useState(false);
  const [showCompositionPicker, setShowCompositionPicker] = useState(false);
  const [showFilingForm, setShowFilingForm] = useState(false);
  const [filingForm, setFilingForm] = useState({
    party_name: "", party_type: "plaintiff", filing_type: "proposals",
    filing_date: "", filing_time: "", folders_count: 0, box_count: 0
  });
  // Service Reports (Εκθέσεις Επίδοσης ανά εναγόμενο)
  const [showServiceReportForm, setShowServiceReportForm] = useState(false);
  const [serviceReportForm, setServiceReportForm] = useState({
    defendant_name: "", bailiff_name: "", report_number: "", report_date: "", notes: ""
  });
  const [showDelete, setShowDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [autoSendEmail, setAutoSendEmail] = useState(false);
  const [related, setRelated] = useState({ main_case: null, children: [], children_count: 0 });
  const [linkingMain, setLinkingMain] = useState(false);
  const [linkGakInput, setLinkGakInput] = useState("");
  const [linkYearInput, setLinkYearInput] = useState("");

  // Πράξη Ορισμού Δικαστή
  const [actLoading] = useState(false);
  const [actIssue, setActIssue] = useState({ open: false, kind: "assignment", format: "docx" });
  const [emailActOpen, setEmailActOpen] = useState(false);
  const [emailActJudges, setEmailActJudges] = useState([]);  // διαθέσιμοι δικαστές για dropdown
  const [emailActForm, setEmailActForm] = useState({
    to_email: "", format: "docx", act_number: "", subject: "", body: ""
  });
  const [emailActSending, setEmailActSending] = useState(false);
  // Αποθηκευμένα παραγόμενα έγγραφα Πράξεων
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const fetchGeneratedDocs = useCallback(async () => {
    if (!id) return;
    try {
      const r = await axios.get(`${API}/cases/${id}/generated-documents`);
      setGeneratedDocs(r.data || []);
    } catch (error) {
      console.error("Failed to fetch generated docs:", error);
    }
  }, [id]);
  useEffect(() => { fetchGeneratedDocs(); }, [fetchGeneratedDocs]);

  const downloadSavedDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/cases/${id}/generated-documents/${docId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Σφάλμα λήψης: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteSavedDoc = async (docId) => {
    if (!window.confirm("Διαγραφή αυτής της αποθηκευμένης Πράξης;")) return;
    try {
      await axios.delete(`${API}/cases/${id}/generated-documents/${docId}`);
      toast.success("Διαγράφηκε");
      fetchGeneratedDocs();
    } catch (e) {
      toast.error("Σφάλμα διαγραφής: " + (e.response?.data?.detail || e.message));
    }
  };
  // Πράξη Ανάθεσης σε Εισηγητή (Πολυμελές sectors 1-3)
  const [rappLoading] = useState(false);
  const [emailRappOpen, setEmailRappOpen] = useState(false);
  const [emailRappForm, setEmailRappForm] = useState({
    to_email: "", format: "docx", act_number: "", subject: "", body: "", include_synthesis: true
  });
  const [emailRappSending, setEmailRappSending] = useState(false);

  useEffect(() => {
    fetchCase();
    fetchRelated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRelated = async () => {
    try {
      const r = await axios.get(`${API}/cases/${id}/related`);
      setRelated(r.data || { main_case: null, children: [], children_count: 0, ignored_count: 0 });
    } catch {
      setRelated({ main_case: null, children: [], children_count: 0 });
    }
  };

  const handleLinkMain = async () => {
    if (!linkGakInput.trim()) {
      toast.error("Παρακαλώ εισάγετε ΓΑΚ κύριας αγωγής");
      return;
    }
    try {
      setLinkingMain(true);
      const res = await axios.post(`${API}/cases/${id}/link-main`, {
        related_main_gak: linkGakInput.trim(),
        related_main_year: linkYearInput.trim(),
      });
      if (res.data?.success) {
        toast.success("Συνδέθηκε με την κύρια αγωγή");
        setLinkGakInput("");
        setLinkYearInput("");
        await fetchCase();
        await fetchRelated();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα σύνδεσης");
    } finally {
      setLinkingMain(false);
    }
  };

  const handleUnlinkMain = async () => {
    try {
      await axios.post(`${API}/cases/${id}/link-main`, { related_main_gak: "", related_main_year: "" });
      toast.success("Αποσύνδεση επιτυχής");
      await fetchCase();
      await fetchRelated();
    } catch {
      toast.error("Σφάλμα αποσύνδεσης");
    }
  };

  // user (Νοέ 2026): αγνόηση/επαναφορά παρεπόμενων δικογράφων (εμφάνιση έως 5)
  const ignoreChild = async (childId, ignore) => {
    try {
      await axios.post(`${API}/cases/${childId}/related-ignore`, { ignore });
      toast.success(ignore ? "Το δικόγραφο αγνοήθηκε" : "Το δικόγραφο επανήλθε");
      fetchRelated();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };
  const restoreIgnored = async () => {
    try {
      const r = await axios.post(`${API}/cases/${id}/related-restore-all`);
      toast.success(`Επαναφέρθηκαν ${r.data?.restored || 0} δικόγραφα`);
      fetchRelated();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };

  const fetchCase = async () => {
    try {
      const response = await axios.get(`${API}/cases/${id}`);
      setCaseData(response.data);
      setEditForm({ ...response.data, mediation_referred: !!(response.data.mediation_referred || response.data.mediation_start) });
      // Φόρτωση δικαστών Προδικαστικού Ελέγχου ανά τύπο δικαστηρίου της υπόθεσης
      try {
        const jr = await axios.get(`${API}/pretrial-judges-list?court_type=${encodeURIComponent(response.data.court_type || "Μονομελές")}`);
        setPretrialJudges(jr.data?.judges || []);
      } catch { /* ignore */ }
    } catch (error) {
      toast.error("Η υπόθεση δεν βρέθηκε");
      navigate("/cases");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = {
        subject: editForm.subject,
        sub_subject: editForm.sub_subject,
        case_type: editForm.case_type,
        plaintiffs: editForm.plaintiffs,
        defendants: editForm.defendants,
        notes: editForm.notes,
        status: editForm.status,
        assigned_judge: editForm.assigned_judge,
        procedure_type: editForm.procedure_type,
        withdrawal_indicator: !!editForm.withdrawal_indicator,
        withdrawal_type: editForm.withdrawal_type || "",
        withdrawal_gak: editForm.withdrawal_gak || "",
        withdrawal_date: editForm.withdrawal_date || "",
        settlement_214a_date: editForm.settlement_214a_date || "",
        mediation_referred: !!(editForm.mediation_referred),
        mediation_start: editForm.mediation_start || "",
        mediation_end: editForm.mediation_end || "",
        mediation_result: editForm.mediation_result || "",
        order_issued: editForm.order_issued,
        order_issue_date: editForm.order_issue_date,
        order_compliance_days: editForm.order_compliance_days ? parseInt(editForm.order_compliance_days) : null
      };
      // Admin can edit all fields
      if (isAdmin) {
        updateData.gak_number = editForm.gak_number;
        updateData.gak_year = editForm.gak_year;
        updateData.eak_number = editForm.eak_number;
        updateData.filing_date = editForm.filing_date;
        updateData.court_type = editForm.court_type;
        updateData.hearing_date = editForm.hearing_date;
        updateData.defendant_residence = editForm.defendant_residence;
        updateData.is_state_party = editForm.is_state_party;
      }
      await axios.put(`${API}/cases/${id}`, updateData);
      toast.success("Οι αλλαγές αποθηκεύτηκαν");
      setEditing(false);

      // Auto-send email if checkbox was checked and judge was assigned
      if (autoSendEmail && editForm.assigned_judge) {
        try {
          const emailRes = await axios.post(`${API}/cases/${id}/send-judge-email`);
          toast.success(emailRes.data.message || "Email ειδοποίησης στάλθηκε");
        } catch (emailErr) {
          toast.error(emailErr.response?.data?.detail || "Αποτυχία αποστολής email");
        }
        setAutoSendEmail(false);
      }

      fetchCase();
    } catch (error) {
      toast.error("Σφάλμα κατά την αποθήκευση");
    } finally {
      setSaving(false);
    }
  };

  const handleSendJudgeEmail = async () => {
    try {
      setSendingEmail(true);
      const response = await axios.post(`${API}/cases/${id}/send-judge-email`);
      toast.success(response.data.message || "Email στάλθηκε επιτυχώς");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Αποτυχία αποστολής email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleClearAssignment = async () => {
    if (!window.confirm("Είστε σίγουροι ότι θέλετε να απαλείψετε την ανάθεση δικαστή από αυτή την υπόθεση;\n\nΘα καθαριστούν τα στοιχεία ανάθεσης και η Πράξη Ορισμού (με audit log).")) return;
    try {
      const r = await axios.post(`${API}/pretrial-tools/cancel-assignment`, { case_id: id, reason: "Απαλοιφή από καρτέλα υπόθεσης" });
      toast.success(`Απαλείφθηκε η ανάθεση: ${r.data?.previous_judge || ""}`);
      fetchCase();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα απαλοιφής ανάθεσης");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/cases/${id}`);
      toast.success("Η υπόθεση διαγράφηκε");
      navigate("/cases");
    } catch (error) {
      toast.error("Σφάλμα κατά τη διαγραφή");
    }
  };

  const handleRecalculate = async () => {
    try {
      const res = await axios.post(`${API}/cases/${id}/recalculate-deadlines`);
      if (res.data.deadlines_count > 0) {
        toast.success(`Επαναυπολογισμός: ${res.data.deadlines_count} προθεσμίες`);
      } else {
        toast.info(res.data.message || "Δεν υπολογίστηκαν προθεσμίες");
      }
      fetchCase();
    } catch { toast.error("Σφάλμα επαναυπολογισμού"); }
  };

  const handleResidenceChange = async (value) => {
    try {
      await axios.put(`${API}/cases/${id}/defendant-residence?residence=${value}`);
      toast.success("Ενημερώθηκε & επαναυπολογίστηκαν οι προθεσμίες");
      fetchCase();
    } catch { toast.error("Σφάλμα ενημέρωσης"); }
  };

  const handleAddFiling = async () => {
    if (!filingForm.party_name) { toast.error("Εισάγετε όνομα διαδίκου"); return; }
    try {
      await axios.post(`${API}/cases/${id}/party-filings`, filingForm);
      toast.success("Η κατάθεση καταχωρήθηκε");
      setShowFilingForm(false);
      setFilingForm({ party_name: "", party_type: "plaintiff", filing_type: "proposals", filing_date: "", filing_time: "", folders_count: 0, box_count: 0 });
      fetchCase();
    } catch { toast.error("Σφάλμα καταχώρησης"); }
  };

  const handleDeleteFiling = async (filingId) => {
    try {
      await axios.delete(`${API}/cases/${id}/party-filings/${filingId}`);
      toast.success("Η καταχώρηση διαγράφηκε");
      fetchCase();
    } catch { toast.error("Σφάλμα διαγραφής"); }
  };

  const handleAddServiceReport = async () => {
    if (!serviceReportForm.defendant_name?.trim()) { toast.error("Επιλέξτε εναγόμενο"); return; }
    try {
      await axios.post(`${API}/cases/${id}/service-reports`, serviceReportForm);
      toast.success("Η έκθεση επίδοσης καταχωρήθηκε");
      setShowServiceReportForm(false);
      setServiceReportForm({ defendant_name: "", bailiff_name: "", report_number: "", report_date: "", notes: "" });
      fetchCase();
    } catch { toast.error("Σφάλμα καταχώρησης"); }
  };

  const handleDeleteServiceReport = async (reportId) => {
    try {
      await axios.delete(`${API}/cases/${id}/service-reports/${reportId}`);
      toast.success("Η έκθεση διαγράφηκε");
      fetchCase();
    } catch { toast.error("Σφάλμα διαγραφής"); }
  };

  const updateDeadlineStatus = async (deadlineType, status) => {
    try {
      await axios.put(`${API}/cases/${id}/deadlines/${deadlineType}/status?status=${status}`);
      toast.success("Η κατάσταση ενημερώθηκε");
      fetchCase();
    } catch (error) {
      toast.error("Σφάλμα κατά την ενημέρωση");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("el-GR", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const getDeadlineStatus = (deadline) => {
    if (deadline.status === "completed") {
      return { label: "Ολοκληρώθηκε", class: "badge-success", icon: CheckCircle2 };
    }
    
    // Επίδοση δικογράφου: "Εν Αναμονή" αντί "Εκπρόθεσμη"
    if (deadline.status === "awaiting" || deadline.never_overdue) {
      return { label: "Εν Αναμονή", class: "bg-slate-100 text-slate-600 border border-slate-200", icon: Clock };
    }
    
    const deadlineDate = new Date(deadline.deadline_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (deadlineDate < today) {
      return { label: "Εκπρόθεσμη", class: "badge-urgent", icon: AlertTriangle };
    }
    
    const daysRemaining = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 7) {
      return { label: `${daysRemaining} ημέρες`, class: "badge-warning", icon: Clock };
    }
    
    return { label: `${daysRemaining} ημέρες`, class: "badge-info", icon: Clock };
  };

  const handlePrintDeadlines = () => {
    if (!caseData?.deadlines?.length) return;
    const printWindow = window.open('', '_blank');
    const rows = caseData.deadlines.map(d => {
      const status = getDeadlineStatus(d);
      const statusText = status.label;
      const isCompleted = d.status === "completed";
      const isOverdue = status.class === "badge-urgent";
      return `<tr style="${isCompleted ? 'color:#16a34a;' : isOverdue ? 'color:#dc2626;font-weight:600;' : ''}">
        <td style="padding:8px;border:1px solid #e2e8f0;">${d.title}</td>
        <td style="padding:8px;border:1px solid #e2e8f0;">${d.description || "-"}</td>
        <td style="padding:8px;border:1px solid #e2e8f0;font-family:monospace;white-space:nowrap;">${d.deadline_start ? `${d.deadline_start} → ${d.deadline_date}` : (d.deadline_date || "-")}</td>
        <td style="padding:8px;border:1px solid #e2e8f0;text-align:center;">
          <span style="padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;${
            isCompleted ? 'background:#dcfce7;color:#16a34a;' : 
            isOverdue ? 'background:#fef2f2;color:#dc2626;' : 'background:#eff6ff;color:#2563eb;'
          }">${statusText}</span>
        </td>
      </tr>`;
    }).join('');
    printWindow.document.write(`<html><head><title>${courtBranch} - ${caseData.gak_number}/${caseData.gak_year}</title>
      <style>body{font-family:'Segoe UI',Tahoma,sans-serif;padding:20px;color:#1e293b}
      h1{font-size:18px;margin-bottom:2px} h2{font-size:14px;color:#64748b;margin-bottom:16px;font-weight:normal}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th{background:#1e40af;color:white;padding:8px;text-align:left;font-weight:600}
      .footer{margin-top:16px;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:8px}
      .info{display:flex;gap:24px;margin-bottom:12px;font-size:12px}
      .info span{color:#64748b} .info strong{color:#1e293b}
      @media print{body{padding:0}}</style></head><body>
      <h1>${courtBranch} - \u0393\u0391\u039A ${caseData.gak_number}/${caseData.gak_year}</h1>
      <h2>${caseData.court_type} - ${caseData.subject || ""}</h2>
      <div class="info">
        <div><span>\u0395\u0391\u039A:</span> <strong>${caseData.eak_number || "-"}</strong></div>
        <div><span>\u0397\u03BC. \u039A\u03B1\u03C4\u03AC\u03B8\u03B5\u03C3\u03B7\u03C2:</span> <strong>${caseData.filing_date || "-"}</strong></div>
        <div><span>\u0395\u03BD\u03AC\u03B3\u03BF\u03BD\u03C4\u03B5\u03C2:</span> <strong>${privacy ? "**" : (caseData.plaintiffs || "-")}</strong></div>
        <div><span>\u0395\u03BD\u03B1\u03B3\u03CC\u03BC\u03B5\u03BD\u03BF\u03B9:</span> <strong>${privacy ? "**" : (caseData.defendants || "-")}</strong></div>
      </div>
      <div style="margin-bottom:12px;font-size:12px;padding:8px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;">
        <strong style="color:#1e293b;">\u0395\u03C0\u03B9\u03C0\u03BB\u03AD\u03BF\u03BD \u03A0\u03BB\u03B7\u03C1\u03BF\u03C6\u03BF\u03C1\u03AF\u03B5\u03C2:</strong>
        <div style="display:flex;gap:24px;margin-top:6px;">
          <div><span style="color:#64748b;">\u0394\u03B9\u03BA\u03AC\u03C3\u03B9\u03BC\u03BF\u03C2:</span> <strong style="color:#1e293b;">${caseData.hearing_date || caseData.last_hearing_date || "\u03A7\u03C9\u03C1\u03AF\u03C2"}</strong></div>
          ${caseData.last_hearing_date ? `<div><span style="color:#64748b;">\u03A4\u03B5\u03BB\u03B5\u03C5\u03C4\u03B1\u03AF\u03B1 \u0394\u03B9\u03BA\u03AC\u03C3\u03B9\u03BC\u03BF\u03C2:</span> <strong style="color:#1e293b;">${caseData.last_hearing_date}</strong></div>` : ""}
          ${caseData.procedure_type ? `<div><span style="color:#64748b;">\u0394\u03B9\u03B1\u03B4\u03B9\u03BA\u03B1\u03C3\u03AF\u03B1:</span> <strong style="color:#1e293b;">${caseData.procedure_type}</strong></div>` : ""}
          ${caseData.assigned_judge ? `<div><span style="color:#64748b;">\u0394\u03B9\u03BA\u03B1\u03C3\u03C4\u03AE\u03C2:</span> <strong style="color:#1e293b;">${caseData.assigned_judge}</strong></div>` : ""}
        </div>
      </div>
      <table><thead><tr>
        <th>\u03A3\u03C4\u03AC\u03B4\u03B9\u03BF</th><th>\u03A0\u03B5\u03C1\u03B9\u03B3\u03C1\u03B1\u03C6\u03AE</th><th>\u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B1</th><th>\u039A\u03B1\u03C4\u03AC\u03C3\u03C4\u03B1\u03C3\u03B7</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div class="footer">\u03A3\u03CD\u03BD\u03BF\u03BB\u03BF: ${caseData.deadlines.length} \u03C0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B5\u03C2 | \u0395\u03BA\u03C4\u03C5\u03C0\u03CE\u03B8\u03B7\u03BA\u03B5: ${new Date().toLocaleDateString('el-GR')} ${new Date().toLocaleTimeString('el-GR')}</div>
      </body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!caseData) return null;

  const handleExportSummary = () => {
    const pf = caseData.party_filings || [];
    const proposals = pf.filter(f => f.filing_type === "proposals");
    const additions = pf.filter(f => f.filing_type === "additions");
    
    const fmtD = (d) => { if (!d) return "-"; const p = d.split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
    
    const deadlineRows = (caseData.deadlines || []).map(d => 
      `<tr><td>${d.title || ""}</td><td>${d.description || ""}</td><td>${d.deadline_start ? fmtD(d.deadline_start) + " → " + fmtD(d.deadline_date) : fmtD(d.deadline_date)}</td><td>${d.status === "completed" ? "\u039F\u03BB\u03BF\u03BA\u03BB\u03B7\u03C1\u03CE\u03B8\u03B7\u03BA\u03B5" : "\u0395\u03BA\u03BA\u03C1\u03B5\u03BC\u03B5\u03AF"}</td></tr>`
    ).join("");
    
    const filingRows = pf.map(f =>
      `<tr><td>${f.party_name || ""}</td><td>${f.filing_type === "proposals" ? "\u03A0\u03C1\u03BF\u03C4\u03AC\u03C3\u03B5\u03B9\u03C2" : "\u03A0\u03C1\u03BF\u03C3\u03B8\u03AE\u03BA\u03B7"}</td><td>${fmtD(f.filing_date)}</td><td>${f.folders_count || 0} \u03C6\u03B1\u03BA. / ${f.box_count || 0} box</td></tr>`
    ).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>\u03A3\u03C5\u03BD\u03BF\u03C0\u03C4\u03B9\u03BA\u03CC \u03A3\u03B7\u03BC\u03B5\u03AF\u03C9\u03BC\u03B1 - \u0393\u0391\u039A ${caseData.gak_number}/${caseData.gak_year}</title>
    <style>
      body{font-family:'Segoe UI',Tahoma,sans-serif;margin:20px;color:#1e293b;font-size:12px}
      h1{color:#1e3a5f;font-size:18px;border-bottom:2px solid #1e3a5f;padding-bottom:8px}
      h2{color:#334155;font-size:14px;margin-top:20px;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
      table{width:100%;border-collapse:collapse;margin:8px 0}
      th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left;font-size:11px}
      th{background:#f1f5f9;font-weight:600}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:8px 0}
      .info-item span{color:#64748b;font-size:11px}
      .info-item strong{display:block;color:#1e293b}
      .footer{margin-top:24px;font-size:10px;color:#94a3b8;text-align:center;border-top:1px solid #e2e8f0;padding-top:8px}
      @media print{body{margin:10mm}}
    </style></head><body>
    <h1>${courtBranch} - \u03A3\u03C5\u03BD\u03BF\u03C0\u03C4\u03B9\u03BA\u03CC \u03A3\u03B7\u03BC\u03B5\u03AF\u03C9\u03BC\u03B1 \u03A5\u03C0\u03CC\u03B8\u03B5\u03C3\u03B7\u03C2</h1>
    <div class="info-grid">
      <div class="info-item"><span>\u0393\u0391\u039A:</span><strong>${caseData.gak_number}/${caseData.gak_year}</strong></div>
      <div class="info-item"><span>\u0395\u0391\u039A:</span><strong>${caseData.eak_number || "-"}</strong></div>
      <div class="info-item"><span>\u0394\u03B9\u03BA\u03B1\u03C3\u03C4\u03AE\u03C1\u03B9\u03BF:</span><strong>${caseData.court_type}</strong></div>
      <div class="info-item"><span>\u0397\u03BC. \u039A\u03B1\u03C4\u03AC\u03B8\u03B5\u03C3\u03B7\u03C2:</span><strong>${caseData.filing_date || "-"}</strong></div>
      <div class="info-item"><span>\u0395\u03BD\u03AC\u03B3\u03BF\u03BD\u03C4\u03B5\u03C2:</span><strong>${privacy ? "**" : (caseData.plaintiffs || "-")}</strong></div>
      <div class="info-item"><span>\u0395\u03BD\u03B1\u03B3\u03CC\u03BC\u03B5\u03BD\u03BF\u03B9:</span><strong>${privacy ? "**" : (caseData.defendants || "-")}</strong></div>
      <div class="info-item"><span>\u0394\u03B9\u03BA\u03AC\u03C3\u03B9\u03BC\u03BF\u03C2:</span><strong>${caseData.hearing_date || caseData.last_hearing_date || "\u03A7\u03C9\u03C1\u03AF\u03C2"}</strong></div>
      <div class="info-item"><span>\u0394\u03B9\u03BA\u03B1\u03C3\u03C4\u03AE\u03C2:</span><strong>${caseData.assigned_judge || "-"}</strong></div>
    </div>
    
    <h2>\u0394\u03B9\u03BA\u03BF\u03BD\u03BF\u03BC\u03B9\u03BA\u03AC \u03A3\u03C4\u03AC\u03B4\u03B9\u03B1 & \u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B5\u03C2</h2>
    <table><thead><tr><th>\u03A3\u03C4\u03AC\u03B4\u03B9\u03BF</th><th>\u03A0\u03B5\u03C1\u03B9\u03B3\u03C1\u03B1\u03C6\u03AE</th><th>\u03A0\u03C1\u03BF\u03B8\u03B5\u03C3\u03BC\u03AF\u03B1</th><th>\u039A\u03B1\u03C4\u03AC\u03C3\u03C4\u03B1\u03C3\u03B7</th></tr></thead>
    <tbody>${deadlineRows || "<tr><td colspan='4'>\u0394\u03B5\u03BD \u03C5\u03C0\u03AC\u03C1\u03C7\u03BF\u03C5\u03BD</td></tr>"}</tbody></table>
    
    <h2>\u039A\u03B1\u03C4\u03B1\u03B8\u03AD\u03C3\u03B5\u03B9\u03C2 \u0394\u03B9\u03B1\u03B4\u03AF\u03BA\u03C9\u03BD</h2>
    ${filingRows ? `<table><thead><tr><th>\u0394\u03B9\u03AC\u03B4\u03B9\u03BA\u03BF\u03C2</th><th>\u03A4\u03CD\u03C0\u03BF\u03C2</th><th>\u0397\u03BC\u03B5\u03C1\u03BF\u03BC\u03B7\u03BD\u03AF\u03B1</th><th>\u03A6\u03AC\u03BA\u03B5\u03BB\u03BF\u03B9/Box</th></tr></thead><tbody>${filingRows}</tbody></table>` : "<p>\u0394\u03B5\u03BD \u03C5\u03C0\u03AC\u03C1\u03C7\u03BF\u03C5\u03BD \u03BA\u03B1\u03C4\u03B1\u03B8\u03AD\u03C3\u03B5\u03B9\u03C2</p>"}
    
    <div class="footer">\u0395\u03BA\u03C4\u03C5\u03C0\u03CE\u03B8\u03B7\u03BA\u03B5: ${new Date().toLocaleDateString('el-GR')} ${new Date().toLocaleTimeString('el-GR')} | ${courtBranch}</div>
    </body></html>`;
    
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.print();
  };

  const handleUpdateHearing = async (newDate) => {
    try {
      await axios.put(`${API}/cases/${id}/hearing`, { hearing_date: newDate });
      toast.success("\u0397 \u03B4\u03B9\u03BA\u03AC\u03C3\u03B9\u03BC\u03BF\u03C2 \u03B5\u03BD\u03B7\u03BC\u03B5\u03C1\u03CE\u03B8\u03B7\u03BA\u03B5");
      fetchCase();
    } catch (error) {
      toast.error("\u03A3\u03C6\u03AC\u03BB\u03BC\u03B1 \u03BA\u03B1\u03C4\u03AC \u03C4\u03B7\u03BD \u03B5\u03BD\u03B7\u03BC\u03AD\u03C1\u03C9\u03C3\u03B7 \u03B4\u03B9\u03BA\u03B1\u03C3\u03AF\u03BC\u03BF\u03C5");
    }
  };

  // Έκδοση Πράξης (Ορισμού Δικαστή/Σύνθεσης ή Εισηγητή) — διάλογος με Αρ. Πράξης + Ημερομηνία Έκδοσης
  const handleGenerateAct = (format) => {
    if (!caseData) return;
    if ((caseData.filing_date || "") < "2026-01-01") { toast.error("Πράξεις Ορισμού εκδίδονται μόνο για υποθέσεις 2026+"); return; }
    // Αν υπάρχει εκδοθείσα («παγωμένη») πράξη → ΛΗΨΗ της ίδιας πράξης, χωρίς επανέκδοση
    if (caseData.assignment_act_number) {
      downloadActFromUrl(`${API}/pretrial-sectors/generate-act/${caseData.id}`, {}, format,
        `${caseData.court_type === "Πολυμελές" ? "Πράξη Ορισμού Σύνθεσης" : "Πράξη Ορισμού"} ${caseData.assignment_act_number}`);
      return;
    }
    setActIssue({ open: true, kind: "assignment", format });
  };
  const reissueAct = (kind = "assignment") => setActIssue({ open: true, kind, format: "docx" });
  const suggestActNumber = async () => {
    try {
      const r = await axios.get(`${API}/pretrial-sectors/next-act-number`, {
        params: { court_type: caseData.court_type || "Μονομελές", year: String(caseData.gak_year || new Date().getFullYear()), branch: (user?.court_branch || "").trim() },
      });
      return Number.isInteger(r.data?.next) ? String(r.data.next) : "";
    } catch { return ""; }
  };
  const confirmActIssue = async ({ actNumber, issueDate, format, includeNote }) => {
    const isRapp = actIssue.kind === "rapporteur";
    const url = isRapp ? `${API}/pretrial-sectors/generate-rapporteur-act/${caseData.id}` : `${API}/pretrial-sectors/generate-act/${caseData.id}`;
    const label = isRapp ? `Πράξη Ορισμού Εισηγητή ${actNumber}` : `Πράξη Ορισμού ${actNumber}`;
    const params = { act_number: actNumber, issue_date: issueDate };
    if (includeNote !== undefined) params.pretrial_note = includeNote;
    const ok = await downloadActFromUrl(url, params, format, label);
    if (ok) { fetchCase(); fetchGeneratedDocs(); }
    return ok;
  };

  // Άνοιγμα διαλόγου αποστολής Πράξης Ορισμού στον δικαστή
  const openEmailActDialog = async () => {
    if (!caseData) return;
    const suggested = caseData.assignment_act_number || "";
    // Φόρτωση όλων των διαθέσιμων δικαστών για ευκολία στο dropdown
    try {
      const jr = await axios.get(`${API}/pretrial-judges-list`, {
        params: { court_type: caseData.court_type || "Μονομελές" }
      });
      setEmailActJudges(jr.data?.judges || []);
    } catch (_) {
      setEmailActJudges([]);
    }
    // Auto-prefill email: 1) assigned_judge_email, 2) προηγούμενο emailed_to, 3) κενό
    const prefillEmail = (caseData.assigned_judge_email && caseData.assigned_judge_email.includes("@"))
      ? caseData.assigned_judge_email
      : (caseData.assignment_act_emailed_to || "");
    setEmailActForm({
      to_email: prefillEmail,
      format: "docx",
      act_number: suggested,
      subject: "",
      body: "",
    });
    setEmailActOpen(true);
  };

  const handleSendActEmail = async () => {
    if (!emailActForm.to_email || !emailActForm.to_email.includes("@")) {
      toast.error("Δώστε έγκυρο email δικαστή");
      return;
    }
    if (!emailActForm.act_number?.trim()) {
      toast.error("Δώστε αριθμό πράξης");
      return;
    }
    try {
      setEmailActSending(true);
      const payload = {
        to_email: emailActForm.to_email.trim(),
        format: emailActForm.format,
        act_number: emailActForm.act_number.trim(),
      };
      if (emailActForm.subject?.trim()) payload.subject = emailActForm.subject.trim();
      if (emailActForm.body?.trim()) payload.body = emailActForm.body.trim();
      const r = await axios.post(`${API}/pretrial-sectors/email-act/${caseData.id}`, payload);
      toast.success(r.data?.message || "Η Πράξη στάλθηκε στον δικαστή");
      setEmailActOpen(false);
      fetchCase();
    } catch (e) {
      // Αναλυτικό error message ώστε ο χρήστης να βλέπει την πραγματική αιτία
      let msg = "Αποτυχία αποστολής";
      if (e.response) {
        // Server απάντησε με error
        const detail = e.response.data?.detail || e.response.data?.message || JSON.stringify(e.response.data || {});
        msg = `Αποτυχία αποστολής (${e.response.status}): ${detail}`;
      } else if (e.request) {
        // Δεν ήρθε καμία απάντηση — timeout ή network
        msg = `Αποτυχία επικοινωνίας με τον server: ${e.message || "no response"}. Πιθανός λόγος: SMTP timeout ή network issue.`;
      } else {
        msg = `Σφάλμα: ${e.message || String(e)}`;
      }
      // console για επιπλέον debugging στο production
      console.error("[email-act] error:", e?.response?.status, e?.response?.data, e?.message);
      toast.error(msg, { duration: 12000 });
    } finally {
      setEmailActSending(false);
    }
  };

  // Πράξη Ορισμού Εισηγητή (Πολυμελές sectors 1-3) — ίδιος διάλογος (Αρ. Πράξης + Ημερομηνία Έκδοσης)
  const handleGenerateRapporteurAct = (format) => {
    if (!caseData) return;
    if (caseData.rapporteur_act_number) {  // «παγωμένη» πράξη → λήψη της ίδιας
      downloadActFromUrl(`${API}/pretrial-sectors/generate-rapporteur-act/${caseData.id}`, {}, format, `Πράξη Ορισμού Εισηγητή ${caseData.rapporteur_act_number}`);
      return;
    }
    setActIssue({ open: true, kind: "rapporteur", format });
  };

  const openEmailRappDialog = async () => {
    if (!caseData) return;
    const suggested = caseData.rapporteur_act_number || "";
    setEmailRappForm({
      to_email: caseData.rapporteur_act_emailed_to || "",
      format: "docx",
      act_number: suggested,
      subject: "",
      body: "",
      include_synthesis: true,
    });
    setEmailRappOpen(true);
  };

  const handleSendRappEmail = async () => {
    if (!emailRappForm.to_email || !emailRappForm.to_email.includes("@")) {
      toast.error("Δώστε έγκυρο email"); return;
    }
    if (!emailRappForm.act_number?.trim()) {
      toast.error("Δώστε αριθμό πράξης"); return;
    }
    try {
      setEmailRappSending(true);
      const payload = {
        to_email: emailRappForm.to_email.trim(),
        format: emailRappForm.format,
        act_number: emailRappForm.act_number.trim(),
        include_synthesis: !!emailRappForm.include_synthesis,
      };
      if (emailRappForm.subject?.trim()) payload.subject = emailRappForm.subject.trim();
      if (emailRappForm.body?.trim()) payload.body = emailRappForm.body.trim();
      const r = await axios.post(`${API}/pretrial-sectors/email-rapporteur-act/${caseData.id}`, payload);
      toast.success(r.data?.message || "Στάλθηκε");
      setEmailRappOpen(false);
      fetchCase();
    } catch (e) {
      let msg = "Αποτυχία αποστολής";
      if (e.response) {
        const detail = e.response.data?.detail || e.response.data?.message || JSON.stringify(e.response.data || {});
        msg = `Αποτυχία αποστολής (${e.response.status}): ${detail}`;
      } else if (e.request) {
        msg = `Αποτυχία επικοινωνίας με τον server: ${e.message || "no response"}`;
      } else {
        msg = `Σφάλμα: ${e.message || String(e)}`;
      }
      console.error("[email-rapporteur-act] error:", e?.response?.status, e?.response?.data, e?.message);
      toast.error(msg, { duration: 12000 });
    } finally {
      setEmailRappSending(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/cases")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 font-mono">
                ΓΑΚ {caseData.gak_number}/{caseData.gak_year}
                <span className="ml-3 text-xl text-blue-700">
                  · ΕΑΚ {caseData.eak_number || "—"}/{caseData.gak_year}
                </span>
              </h1>
              <Badge className={caseData.court_type === "Μονομελές" ? "court-mono" : "court-poly"}>
                {caseData.court_type}
              </Badge>
              <Badge className={
                caseData.status === "active" ? "badge-success" :
                caseData.status === "closed" ? "badge-info" : "bg-slate-100 text-slate-600"
              }>
                {caseData.status === "active" ? "Ενεργή" : 
                 caseData.status === "closed" ? "Κλειστή" : "Αρχειοθετημένη"}
              </Badge>
            </div>
            {caseData.eak_number && (
              <p className="text-slate-500 mt-1 font-mono">ΕΑΚ: {caseData.eak_number}</p>
            )}
            {/* Ειδοποίηση απόστασης από δικάσιμο */}
            {caseData.hearing_date && (
              <div className={`mt-2 px-3 py-1.5 rounded-lg inline-flex items-center gap-2 ${
                caseData.days_to_hearing <= 0 ? "bg-red-100 text-red-700" :
                caseData.days_to_hearing <= 7 ? "bg-amber-100 text-amber-700" :
                caseData.days_to_hearing <= 30 ? "bg-blue-100 text-blue-700" :
                "bg-emerald-100 text-emerald-700"
              }`}>
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Δικάσιμος: {formatDate(caseData.hearing_date).split(",")[0]}
                  {caseData.days_to_hearing !== null && (
                    <span className="ml-2">
                      ({caseData.days_to_hearing <= 0 
                        ? "Έχει παρέλθει!" 
                        : `σε ${caseData.days_to_hearing} ημέρες`})
                    </span>
                  )}
                </span>
                <button
                  onClick={() => {
                    const newDate = prompt("Νέα ημερομηνία δικασίμου (YYYY-MM-DD):", caseData.hearing_date || "");
                    if (newDate && /^\d{4}-\d{2}-\d{2}$/.test(newDate)) handleUpdateHearing(newDate);
                  }}
                  className="ml-2 p-1 rounded hover:bg-white/50 transition-colors"
                  title="Τροποποίηση Δικασίμου"
                  data-testid="change-hearing-btn"
                >
                  <CalendarDays className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {!caseData.hearing_date && !caseData.pinakio_id && !caseData.pinakio_assigned_at && !caseData.last_hearing_date && (
              <button
                onClick={() => {
                  const newDate = prompt("Ορισμός ημερομηνίας δικασίμου (YYYY-MM-DD):");
                  if (newDate && /^\d{4}-\d{2}-\d{2}$/.test(newDate)) handleUpdateHearing(newDate);
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                data-testid="set-hearing-btn"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Ορισμός Δικασίμου
              </button>
            )}
            {!caseData.hearing_date && (caseData.pinakio_id || caseData.pinakio_assigned_at || caseData.last_hearing_date) && (
              <p className="mt-2 text-xs text-amber-700 italic">
                ⓘ Η υπόθεση είναι ήδη σε πινάκιο — η δικάσιμος καθορίζεται από το πινάκιο.
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">
                <X className="w-4 h-4 mr-2" />
                Ακύρωση
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Αποθήκευση..." : "Αποθήκευση"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-btn">
                <Edit className="w-4 h-4 mr-2" />
                Επεξεργασία
              </Button>
              {isAdmin && (
                <Button variant="destructive" onClick={() => setShowDelete(true)} data-testid="delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Διαγραφή
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Residence + Recalculate bar */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg border">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600 font-medium">Κάτοικος Εξωτερικού/Αγνώστου:</span>
          <Select value={caseData?.defendant_residence || "domestic"} onValueChange={handleResidenceChange}>
            <SelectTrigger className="w-[130px] h-8 text-sm" data-testid="residence-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="domestic">Οχι</SelectItem>
              <SelectItem value="abroad">Ναι (Εξωτ.)</SelectItem>
              <SelectItem value="unknown">Ναι (Αγνώστου)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={handleRecalculate} data-testid="recalculate-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Επαναυπολογισμός Προθεσμιών
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Case Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Στοιχεία Υπόθεσης
                <span className="ml-auto flex items-center gap-2">
                  {caseData.solon_synced_at && <span className="text-[11px] font-normal text-emerald-700" data-testid="solon-synced-at">SOLON: {new Date(caseData.solon_synced_at).toLocaleDateString("el-GR")}</span>}
                  <SolonCheckButton caseId={id} caseData={caseData} onUpdated={fetchCase} />
                  {caseData.solon_docket?.aa && (
                    <Link to="/dockets" className="inline-flex items-center gap-1 text-xs rounded-full border border-sky-300 bg-sky-50 text-sky-800 px-2.5 py-1 hover:bg-sky-100" title={`${caseData.solon_docket.docket_label} · ${caseData.solon_docket.room}`} data-testid="solon-docket-badge">
                      Έκθεμα SOLON: Α/Α {caseData.solon_docket.aa} · {caseData.solon_docket.hearing_date_gr} {caseData.solon_docket.time}{caseData.solon_docket.presence ? ` · ${caseData.solon_docket.presence}` : ""}
                    </Link>
                  )}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Admin-only core fields */}
              {editing && isAdmin && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div>
                    <Label className="text-blue-700 text-xs font-semibold">ΓΑΚ</Label>
                    <Input value={editForm.gak_number || ""} onChange={e => setEditForm({...editForm, gak_number: e.target.value})} data-testid="edit-gak-number" />
                  </div>
                  <div>
                    <Label className="text-blue-700 text-xs font-semibold">Έτος ΓΑΚ</Label>
                    <Input value={editForm.gak_year || ""} onChange={e => setEditForm({...editForm, gak_year: e.target.value})} data-testid="edit-gak-year" />
                  </div>
                  <div>
                    <Label className="text-blue-700 text-xs font-semibold">ΕΑΚ</Label>
                    <Input value={editForm.eak_number || ""} onChange={e => setEditForm({...editForm, eak_number: e.target.value})} data-testid="edit-eak-number" />
                  </div>
                  <div>
                    <Label className="text-blue-700 text-xs font-semibold">Τύπος</Label>
                    <Select value={editForm.court_type || "Μονομελές"} onValueChange={v => setEditForm({...editForm, court_type: v})}>
                      <SelectTrigger data-testid="edit-court-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                        <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Hearing date - admin editable */}
              {editing && isAdmin && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div>
                    <Label className="text-blue-700 text-xs font-semibold">Δικάσιμος</Label>
                    <DateInput value={editForm.hearing_date || ""} onChange={e => setEditForm({...editForm, hearing_date: e.target.value})} data-testid="edit-hearing-date" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-500">Ημερομηνία Κατάθεσης</Label>
                  {editing && isAdmin ? (
                    <DateInput value={editForm.filing_date || ""} onChange={e => setEditForm({...editForm, filing_date: e.target.value})} />
                  ) : (
                    <p className="font-medium">{formatDate(caseData.filing_date)}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-500">Διαδικασία</Label>
                  {editing ? (
                    <Input
                      value={editForm.procedure_type || ""}
                      onChange={(e) => setEditForm({...editForm, procedure_type: e.target.value})}
                      placeholder="Τακτική / Ειδική"
                    />
                  ) : (
                    <p className="font-medium">{caseData.procedure_type || "-"}</p>
                  )}
                </div>
                {/* Παραίτηση ΝΑΙ/ΟΧΙ + είδος/λεπτομέρειες (iter109) */}
                <WithdrawalFields editing={editing} form={editForm} setForm={setEditForm} data={caseData} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Κατοικία Εναγομένου</Label>
                  {editing && isAdmin ? (
                    <Select value={editForm.defendant_residence || "domestic"} onValueChange={v => setEditForm({...editForm, defendant_residence: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="domestic">Εσωτερικό (30 ημ.)</SelectItem>
                        <SelectItem value="abroad">Εξωτερικό (30 ημ.)</SelectItem>
                        <SelectItem value="unknown">Αγνώστου Διαμονής (30 ημ.)</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="font-medium">
                      {caseData.defendant_residence === "abroad" ? "Εξωτερικό (30 ημ. επίδοση)" : 
                       caseData.defendant_residence === "unknown" ? "Αγνώστου Διαμονής (30 ημ. επίδοση)" :
                       "Εσωτερικό (30 ημ. επίδοση)"}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-500">Διάδικος το Δημόσιο</Label>
                  {editing && isAdmin ? (
                    <Select value={editForm.is_state_party ? "yes" : "no"} onValueChange={v => setEditForm({...editForm, is_state_party: v === "yes"})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">Όχι</SelectItem>
                        <SelectItem value="yes">Ναι</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className={`font-medium ${caseData.is_state_party ? "text-amber-600" : ""}`}>
                      {caseData.is_state_party ? "Ναι (εξαίρεση 1/7-15/9)" : "Όχι"}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-slate-500">Αντικείμενο</Label>
                {editing ? (
                  <Input
                    value={editForm.subject || ""}
                    onChange={(e) => setEditForm({...editForm, subject: e.target.value})}
                    placeholder="Αντικείμενο υπόθεσης"
                  />
                ) : (
                  <p className="font-medium">{caseData.subject || "-"}</p>
                )}
              </div>

              <div>
                <Label className="text-slate-500">Υποαντικείμενο</Label>
                {editing ? (
                  <Input
                    value={editForm.sub_subject || ""}
                    onChange={(e) => setEditForm({...editForm, sub_subject: e.target.value})}
                    placeholder="Υποαντικείμενο"
                  />
                ) : (
                  <p className="font-medium">{caseData.sub_subject || "-"}</p>
                )}
              </div>

              <div>
                <Label className="text-slate-500">Είδος Δικογραφίας</Label>
                {editing ? (
                  <Input
                    value={editForm.case_type || ""}
                    onChange={(e) => setEditForm({...editForm, case_type: e.target.value})}
                    placeholder="Είδος δικογραφίας"
                  />
                ) : (
                  <p className="font-medium">{caseData.case_type || "-"}</p>
                )}
              </div>

              <div>
                <Label className="text-slate-500">Ορισμένος Δικαστής {caseData.assigned_judge_grade && <span className="text-xs text-slate-400">({caseData.assigned_judge_grade})</span>}</Label>
                {editing ? (
                  <div className="space-y-2">
                    <Select
                      value={editForm.assigned_judge_id || ""}
                      onValueChange={(jid) => {
                        const j = pretrialJudges.find(x => x.id === jid);
                        if (j) {
                          setEditForm({
                            ...editForm,
                            assigned_judge: j.full_name,
                            assigned_judge_id: j.id,
                            assigned_judge_grade: j.grade || "",
                            assigned_judge_email: j.email || "",
                          });
                        } else if (jid === "__none__") {
                          setEditForm({...editForm, assigned_judge: "", assigned_judge_id: "", assigned_judge_grade: "", assigned_judge_email: ""});
                        }
                      }}
                    >
                      <SelectTrigger data-testid="assigned-judge-select">
                        <SelectValue placeholder={pretrialJudges.length ? "Επιλέξτε από Δικαστές Προδικαστικού Ελέγχου" : "Δεν υπάρχουν καταχωρημένοι δικαστές"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__none__">— Χωρίς ορισμό —</SelectItem>
                        {pretrialJudges.map(j => (
                          <SelectItem key={j.id} value={j.id}>
                            <JudgeOptionLabel judge={j} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editForm.assigned_judge && (
                      <label className="flex items-center gap-2 cursor-pointer" data-testid="auto-send-email-checkbox">
                        <input
                          type="checkbox"
                          checked={autoSendEmail}
                          onChange={(e) => setAutoSendEmail(e.target.checked)}
                          className="rounded border-slate-300 text-primary focus:ring-primary"
                        />
                        <Mail className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-slate-600">Αποστολή email ειδοποίησης στον δικαστή</span>
                      </label>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{caseData.assigned_judge || "—"}</p>
                    {caseData.court_type === "Πολυμελές" && !editing && (caseData.filing_date || "") >= "2026-01-01" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1 border-purple-500 text-purple-700"
                        onClick={() => setShowCompositionPicker(true)}
                        data-testid="open-composition-picker-btn"
                      >
                        <Users className="w-3 h-3" />
                        {caseData.composition_label ? "Αλλαγή Σύνθεσης" : "Ορισμός Σύνθεσης"}
                      </Button>
                    )}
                    {!caseData.assigned_judge && caseData.pretrial_eligible === false && (
                      <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="pretrial-ineligible-note">
                        {caseData.pretrial_ineligible_reason || "Δεν υπάγεται σε προδικαστικό έλεγχο"}
                      </span>
                    )}
                    {caseData.pretrial_warning && (
                      <span className="text-xs text-orange-800 bg-orange-50 border border-orange-300 rounded px-2 py-1 max-w-xl" data-testid="pretrial-warning-note">
                        {caseData.pretrial_warning}
                      </span>
                    )}
                    {!caseData.assigned_judge && caseData.pretrial_eligible !== false && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1 border-blue-500 text-blue-700"
                        onClick={() => setShowJudgePicker(true)}
                        data-testid="open-judge-picker-btn"
                      >
                        <UserCheck className="w-3 h-3" />
                        {caseData.court_type === "Πολυμελές" ? "Ορισμός Εισηγητή" : "Ορισμός Δικαστή"}
                      </Button>
                    )}
                    {caseData.assigned_judge && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={handleSendJudgeEmail}
                        disabled={sendingEmail}
                        data-testid="send-judge-email-btn"
                      >
                        {sendingEmail ? (<Loader2 className="w-3 h-3 animate-spin" />) : (<Mail className="w-3 h-3" />)}
                        {sendingEmail ? "Αποστολή..." : "Email"}
                      </Button>
                    )}
                    {caseData.assigned_judge && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1 border-red-500 text-red-700 hover:bg-red-50"
                        onClick={handleClearAssignment}
                        data-testid="clear-judge-btn"
                        title="Απαλοιφή ανάθεσης δικαστή (επαναφέρει την υπόθεση σε προ-ορισμού)"
                      >
                        <XCircle className="w-3 h-3" />
                        Απαλοιφή Ανάθεσης
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Δικαστής Έδρας (ορίζεται ανά πινάκιο/δικάσιμο — Πινάκια ή Εργαλεία Συστήματος) */}
              {!editing && (
                <div data-testid="bench-judge-section">
                  <Label className="text-slate-500">Δικαστής Έδρας</Label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium" data-testid="bench-judge-value">{caseData.bench_judge || "—"}</p>
                    {caseData.bench_judge && caseData.bench_judge_source === "auto_pretrial" && (
                      <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50" data-testid="bench-judge-auto-badge">
                        αυτόματα από προδικαστικό
                      </Badge>
                    )}
                    {!caseData.bench_judge && (
                      <span className="text-[11px] text-slate-400 italic">ορίζεται ανά πινάκιο/δικάσιμο από τη σελίδα Πινάκια</span>
                    )}
                  </div>
                </div>
              )}

              {/* Σύνθεση Πολυμελούς (ανά πινάκιο/δικάσιμο — «Ανάθεση σε Σύνθεση» από τα Πινάκια) */}
              {!editing && caseData.composition_label && (
                <div data-testid="composition-section">
                  <Label className="text-slate-500">Σύνθεση Πολυμελούς</Label>
                  <p className="font-medium" data-testid="composition-value">{caseData.composition_label}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(caseData.composition_members || []).map((m, i) => (
                      <Badge key={i} variant="outline" className={`text-[10px] ${m.excluded ? "border-orange-300 text-orange-800 bg-orange-50 line-through" : m.role === "alternate" ? "border-slate-300 text-slate-500" : "border-violet-300 text-violet-800 bg-violet-50"}`}
                             title={m.excluded ? `Εξαιρέθηκε για τη δικάσιμο — ${m.excluded_reason || "Μονομελές ίδια ημέρα"}` : ""}
                             data-testid={m.excluded ? `composition-member-excluded-${i}` : undefined}>
                        {m.name}{m.grade ? ` · ${m.grade}` : ""}{m.role === "alternate" ? " (αναπλ.)" : ""}{m.excluded ? " (εξαιρέθηκε — Μονομελές ίδια ημέρα)" : ""}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* «ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ» + Εξαιρέσεις Δικαστών (μόνο για αυτή την υπόθεση) */}
              {!editing && (caseData.assigned_judge || caseData.bench_judge || caseData.composition_label) && (
                <JudgeExclusionsCard caseId={id} isAdmin={isAdmin} onChanged={fetchCase} />
              )}

              {/* Πράξη Ορισμού Δικαστή (μόνο για υποθέσεις 2026+ που υπάγονται σε προδικαστικό έλεγχο Ν.5221/2025) */}
              {!editing && (caseData.filing_date || "") >= "2026-01-01" && caseData.pretrial_eligible !== false && (
                <div className="border-t pt-4" data-testid="assignment-act-section">
                  <Label className="text-slate-500 font-semibold flex items-center gap-2">
                    <Gavel className="w-4 h-4 text-blue-700" />
                    {caseData.court_type === "Πολυμελές" ? "Πράξη Ορισμού Σύνθεσης" : "Πράξη Ορισμού Δικαστή"} (άρθρο 237 ΚΠολΔ)
                  </Label>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <div className="text-sm">
                      <span className="text-slate-500">Αρ. Πράξης: </span>
                      {caseData.assignment_act_number ? (
                        <>
                          <Badge variant="outline" className="font-mono text-[11px] border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="assignment-act-number">
                            {caseData.assignment_act_number}/{caseData.assignment_act_year || caseData.gak_year}
                          </Badge>
                          <ActNumberButton caseId={caseData.id} kind="assignment" number={caseData.assignment_act_number} issueDate={caseData.assignment_act_issue_date}
                            year={caseData.assignment_act_year || caseData.gak_year} gak={`${caseData.gak_number}/${caseData.gak_year}`}
                            onSaved={() => { fetchCase(); fetchGeneratedDocs(); }} />
                          <ActNumberDeleteButton caseId={caseData.id} kind="assignment" number={caseData.assignment_act_number}
                            year={caseData.assignment_act_year || caseData.gak_year} gak={`${caseData.gak_number}/${caseData.gak_year}`}
                            onDeleted={() => { fetchCase(); fetchGeneratedDocs(); }} />
                        </>
                      ) : (
                        <span className="text-slate-400 italic">— δεν έχει εκδοθεί ακόμη —</span>
                      )}
                      {(caseData.assignment_act_issue_date || caseData.assignment_act_generated_at) && (
                        <span className="text-[11px] text-slate-400 ml-2" data-testid="assignment-act-issued-on">
                          (εκδόθηκε: {formatDate((caseData.assignment_act_issue_date || caseData.assignment_act_generated_at).substring(0,10)).split(",")[0]})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleGenerateAct("docx")}
                        disabled={actLoading}
                        title="Εξαγωγή Πράξης σε DOCX (Word)"
                        data-testid="export-act-docx-btn"
                      >
                        {actLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                        DOCX
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleGenerateAct("pdf")}
                        disabled={actLoading}
                        title="Εξαγωγή Πράξης σε PDF"
                        data-testid="export-act-pdf-btn"
                      >
                        <Download className="w-3 h-3" />
                        PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleGenerateAct("odt")}
                        disabled={actLoading}
                        title="Εξαγωγή Πράξης σε ODT (OpenDocument)"
                        data-testid="export-act-odt-btn"
                      >
                        <Printer className="w-3 h-3" />
                        ODT
                      </Button>
                      {caseData.assignment_act_number && (
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                          onClick={() => reissueAct("assignment")} disabled={actLoading}
                          title="Επανέκδοση της Πράξης με νέο αριθμό/ημερομηνία (αντικαθιστά την αποθηκευμένη)" data-testid="reissue-act-btn">
                          <RefreshCw className="w-3 h-3" />
                          Επανέκδοση
                        </Button>
                      )}
                      <Button
                        variant="default"
                        size="sm"
                        className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700"
                        onClick={openEmailActDialog}
                        disabled={actLoading}
                        title="Αποστολή Πράξης στον δικαστή για προγραμματισμό προδικαστικού"
                        data-testid="email-act-btn"
                      >
                        <Mail className="w-3 h-3" />
                        Αποστολή στον Δικαστή
                      </Button>
                    </div>
                  </div>
                  {caseData.assignment_act_emailed_at && (
                    <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Στάλθηκε ({caseData.assignment_act_emailed_format?.toUpperCase() || "DOCX"}) στις {formatDate(caseData.assignment_act_emailed_at.substring(0,10)).split(",")[0]} → <strong>{caseData.assignment_act_emailed_to}</strong>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-2">
                    Ο αριθμός πράξης προτείνεται αυτόματα ως ο επόμενος διαθέσιμος για το {caseData.gak_year}, αλλά μπορείτε να τον τροποποιήσετε πριν την εξαγωγή.
                  </p>
                </div>
              )}

              {/* ΑΝΑΧΡΕΩΣΗ — ΠΡΑΞΗ ΝΕΟΥ ΟΡΙΣΜΟΥ (ξεχωριστή αρίθμηση, εκδίδεται ανά δικάσιμο από Πινάκια/Προδικαστικό) */}
              {!editing && caseData.reassignment_act_number && (
                <div className="border-t pt-4 bg-orange-50/50 -mx-4 px-4 py-3" data-testid="reassignment-section">
                  <Label className="text-slate-600 font-semibold flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 text-orange-700" />
                    ΑΝΑΧΡΕΩΣΗ — Πράξη Νέου Ορισμού {caseData.court_type === "Πολυμελές" ? "Εισηγητή" : "Δικαστή"}
                  </Label>
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-sm">
                    <div>
                      <span className="text-slate-500">ΑΡ. ΠΡΑΞΗΣ ΑΝΑΧΡΕΩΣΗΣ: </span>
                      <Badge variant="outline" className="font-mono text-[11px] border-orange-400 text-orange-800 bg-orange-50" data-testid="reassignment-act-number">
                        {caseData.reassignment_act_number}/{caseData.reassignment_act_year || caseData.gak_year}
                      </Badge>
                      <ActNumberButton caseId={caseData.id} kind="reassignment" number={caseData.reassignment_act_number} issueDate={caseData.reassignment_issue_date}
                        year={caseData.reassignment_act_year || caseData.gak_year} gak={`${caseData.gak_number}/${caseData.gak_year}`}
                        onSaved={() => { fetchCase(); fetchGeneratedDocs(); }} />
                      <ActNumberDeleteButton caseId={caseData.id} kind="reassignment" number={caseData.reassignment_act_number}
                        year={caseData.reassignment_act_year || caseData.gak_year} gak={`${caseData.gak_number}/${caseData.gak_year}`}
                        onDeleted={() => { fetchCase(); fetchGeneratedDocs(); }} />
                      {caseData.reassignment_issue_date && (
                        <span className="text-[11px] text-slate-400 ml-2">(εκδόθηκε: {formatDate(caseData.reassignment_issue_date).split(",")[0]})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => downloadReassignmentAct(caseData.id, "docx")}
                              title="Λήψη Πράξης ΑΝΑΧΡΕΩΣΗΣ σε DOCX" data-testid="export-reassign-docx-btn">
                        <FileText className="w-3 h-3" />DOCX
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => downloadReassignmentAct(caseData.id, "pdf")}
                              title="Λήψη Πράξης ΑΝΑΧΡΕΩΣΗΣ σε PDF" data-testid="export-reassign-pdf-btn">
                        <Download className="w-3 h-3" />PDF
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-700 mt-2" data-testid="reassignment-from-to">
                    <span className="text-slate-500">Αντικατάσταση: </span>
                    <strong>{p(caseData.reassigned_from) || "—"}</strong>{caseData.reassigned_from_grade ? ` (${caseData.reassigned_from_grade})` : ""}
                    <span className="mx-1 text-orange-700">→</span>
                    <strong>{p(caseData.assigned_judge) || "—"}</strong>{caseData.assigned_judge_grade ? ` (${caseData.assigned_judge_grade})` : ""}
                  </p>
                  {caseData.reassignment_reason_text && (
                    <p className="text-[11px] text-slate-500 mt-1">Λόγος: {caseData.reassignment_reason_text}</p>
                  )}
                </div>
              )}

              {/* Πράξη Ανάθεσης Εισηγητή — Πολυμελές, μετά την Πράξη Σύνθεσης */}
              {!editing && (caseData.filing_date || "") >= "2026-01-01"
                && caseData.court_type === "Πολυμελές"
                && caseData.assignment_act_number
                && caseData.assignment_act_sector_number !== 4 && (
                <div className="border-t pt-4 bg-amber-50/40 -mx-4 px-4 py-3" data-testid="rapporteur-act-section">
                  <Label className="text-slate-600 font-semibold flex items-center gap-2">
                    <Gavel className="w-4 h-4 text-amber-700" />
                    Πράξη Ανάθεσης σε Εισηγητή <span className="text-[10px] font-normal text-amber-700">(από τον Πρόεδρο της Σύνθεσης)</span>
                  </Label>
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <div className="text-sm">
                      <span className="text-slate-500">Αρ. Πράξης: </span>
                      {caseData.rapporteur_act_number ? (
                        <>
                          <Badge variant="outline" className="font-mono text-[11px] border-amber-400 text-amber-700 bg-amber-50" data-testid="rapporteur-act-number">
                            {caseData.rapporteur_act_number}/{caseData.rapporteur_act_year || caseData.gak_year}
                          </Badge>
                          <ActNumberButton caseId={caseData.id} kind="rapporteur" number={caseData.rapporteur_act_number} issueDate={caseData.rapporteur_act_issue_date}
                            year={caseData.rapporteur_act_year || caseData.gak_year} gak={`${caseData.gak_number}/${caseData.gak_year}`}
                            onSaved={() => { fetchCase(); fetchGeneratedDocs(); }} />
                          <ActNumberDeleteButton caseId={caseData.id} kind="rapporteur" number={caseData.rapporteur_act_number}
                            year={caseData.rapporteur_act_year || caseData.gak_year} gak={`${caseData.gak_number}/${caseData.gak_year}`}
                            onDeleted={() => { fetchCase(); fetchGeneratedDocs(); }} />
                        </>
                      ) : (
                        <span className="text-slate-400 italic">— δεν έχει εκδοθεί ακόμη —</span>
                      )}
                      {(caseData.rapporteur_act_issue_date || caseData.rapporteur_act_generated_at) && (
                        <span className="text-[11px] text-slate-400 ml-2" data-testid="rapporteur-act-issued-on">
                          (εκδόθηκε: {formatDate((caseData.rapporteur_act_issue_date || caseData.rapporteur_act_generated_at).substring(0,10)).split(",")[0]})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleGenerateRapporteurAct("docx")}
                        disabled={rappLoading}
                        title="Εξαγωγή Πράξης Ανάθεσης Εισηγητή σε DOCX"
                        data-testid="export-rapp-docx-btn"
                      >
                        {rappLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
                        DOCX
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleGenerateRapporteurAct("pdf")}
                        disabled={rappLoading}
                        title="Εξαγωγή Πράξης Ανάθεσης Εισηγητή σε PDF"
                        data-testid="export-rapp-pdf-btn"
                      >
                        <Download className="w-3 h-3" />
                        PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => handleGenerateRapporteurAct("odt")}
                        disabled={rappLoading}
                        title="Εξαγωγή Πράξης Ανάθεσης Εισηγητή σε ODT"
                        data-testid="export-rapp-odt-btn"
                      >
                        <Printer className="w-3 h-3" />
                        ODT
                      </Button>
                      {caseData.rapporteur_act_number && (
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                          onClick={() => reissueAct("rapporteur")} disabled={rappLoading}
                          title="Επανέκδοση της Πράξης Εισηγητή με νέο αριθμό/ημερομηνία" data-testid="reissue-rapp-act-btn">
                          <RefreshCw className="w-3 h-3" />
                          Επανέκδοση
                        </Button>
                      )}
                      <Button
                        variant="default"
                        size="sm"
                        className="h-7 text-xs gap-1 bg-amber-600 hover:bg-amber-700"
                        onClick={openEmailRappDialog}
                        disabled={rappLoading}
                        title="Αποστολή Πράξης Ανάθεσης Εισηγητή"
                        data-testid="email-rapp-btn"
                      >
                        <Mail className="w-3 h-3" />
                        Αποστολή στον Εισηγητή
                      </Button>
                    </div>
                  </div>
                  {caseData.rapporteur_act_emailed_at && (
                    <div className="mt-2 text-[11px] text-amber-700 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Στάλθηκε ({caseData.rapporteur_act_emailed_format?.toUpperCase() || "DOCX"}) στις {formatDate(caseData.rapporteur_act_emailed_at.substring(0,10)).split(",")[0]} → <strong>{caseData.rapporteur_act_emailed_to}</strong>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-500 mt-2">
                    Η Πράξη Ανάθεσης ακολουθεί την ίδια αύξουσα σειρά αρίθμησης με τις Πράξεις Σύνθεσης (επόμενος αριθμός για το {caseData.gak_year}).
                  </p>
                </div>
              )}

              {/* Αποθηκευμένες Πράξεις (αρχείο για re-download / διαγραφή) */}
              {!editing && generatedDocs && generatedDocs.length > 0 && (
                <div className="border-t pt-4" data-testid="saved-acts-section">
                  <Label className="text-slate-600 font-semibold flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-slate-700" />
                    Αποθηκευμένες Πράξεις ({generatedDocs.length})
                  </Label>
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {generatedDocs.slice().reverse().map(d => (
                      <div key={d.id} className="flex items-center justify-between gap-2 p-2 rounded border bg-slate-50 text-xs"
                        data-testid={`saved-doc-${d.id}`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Badge variant="outline" className="text-[10px] uppercase border-slate-300">
                            {d.format || "?"}
                          </Badge>
                          {d.kind === "rapporteur" ? (
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">Εισηγητή</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50">Ορισμού</Badge>
                          )}
                          <span className="font-mono text-[10px] text-emerald-700">
                            {d.act_number}/{d.act_year}
                          </span>
                          <span className="text-slate-500 truncate flex-1" title={d.filename}>
                            {d.filename}
                          </span>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">
                            {Math.round((d.size || 0)/1024)} KB · {(d.generated_at || "").substring(0,10)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-blue-700 hover:bg-blue-100"
                            onClick={() => downloadSavedDoc(d.id, d.filename)}
                            title="Λήψη αρχείου"
                            data-testid={`saved-doc-download-${d.id}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-red-700 hover:bg-red-100"
                            onClick={() => deleteSavedDoc(d.id)}
                            title="Διαγραφή αποθηκευμένης Πράξης"
                            data-testid={`saved-doc-delete-${d.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">
                    Κάθε φορά που δημιουργείτε Πράξη Ορισμού/Σύνθεσης/Ανάθεσης Εισηγητή, αποθηκεύεται αυτόματα εδώ και μπορείτε να την κατεβάσετε εκ νέου ή να την διαγράψετε.
                  </p>
                </div>
              )}

              {/* Υπαγωγή σε Εκούσια Διαμεσολάβηση (ΝΑΙ/ΟΧΙ + ημερομηνίες) — μετράται στα Στατιστικά */}
              <MediationFields editing={editing} form={editForm} setForm={setEditForm} data={caseData} />

              {/* Εξωδική επίλυση 214Α ΚΠολΔ (iter109) */}
              <Settlement214aField editing={editing} form={editForm} setForm={setEditForm} data={caseData} />

              {/* Έκδοση Διάταξης - Μόνο για ΓΑΚ >=2026 */}
              {(parseInt(caseData.gak_year) >= 2026 || editing) && (
                <div className="border-t pt-4">
                  <Label className="text-slate-500 font-semibold">Έκδοση Διάταξης (Άρθρο 237 §3)</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Εκδόθηκε Διάταξη;</Label>
                      {editing ? (
                        <Select value={editForm.order_issued ? "yes" : "no"} onValueChange={v => setEditForm({...editForm, order_issued: v === "yes"})}>
                          <SelectTrigger data-testid="order-issued-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no">Όχι</SelectItem>
                            <SelectItem value="yes">Ναι</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className={`font-medium text-sm ${caseData.order_issued ? "text-indigo-600" : ""}`}>
                          {caseData.order_issued ? "Ναι" : "Όχι"}
                        </p>
                      )}
                    </div>
                    {(editing ? editForm.order_issued : caseData.order_issued) && (
                      <>
                        <div>
                          <Label className="text-slate-400 text-xs">Ημερομηνία Έκδοσης</Label>
                          {editing ? (
                            <DateInput value={editForm.order_issue_date || ""} 
                              onChange={e => setEditForm({...editForm, order_issue_date: e.target.value})}
                              data-testid="order-issue-date-input" />
                          ) : (
                            <p className="font-medium text-sm">{caseData.order_issue_date ? formatDate(caseData.order_issue_date) : "-"}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">Ημέρες Συμμόρφωσης (ελάχ. 10)</Label>
                          {editing ? (
                            <Input type="number" min="10" value={editForm.order_compliance_days || ""} 
                              onChange={e => setEditForm({...editForm, order_compliance_days: e.target.value})}
                              placeholder="π.χ. 15"
                              data-testid="order-compliance-days-input" />
                          ) : (
                            <p className="font-medium text-sm">{caseData.order_compliance_days ? `${caseData.order_compliance_days} ημέρες` : "-"}</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {caseData.order_issued && caseData.order_issue_date && caseData.order_compliance_days && (
                    <p className="text-xs text-indigo-600 mt-1">
                      Προθεσμία συμμόρφωσης: {caseData.order_compliance_days} ημέρες από {formatDate(caseData.order_issue_date)}
                    </p>
                  )}
                </div>
              )}

              {editing && (
                <div>
                  <Label className="text-slate-500">Κατάσταση</Label>
                  <Select 
                    value={editForm.status} 
                    onValueChange={(value) => setEditForm({...editForm, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ενεργή</SelectItem>
                      <SelectItem value="closed">Κλειστή</SelectItem>
                      <SelectItem value="archived">Αρχειοθετημένη</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SOLON.GOV — Πορεία Υπόθεσης (9 καρτέλες, αποθηκευμένες από τον τελευταίο έλεγχο) */}
          {caseData.solon_tabs && (
            <Card data-testid="solon-case-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="w-5 h-5" />
                  SOLON.GOV — Πορεία Υπόθεσης
                  <span className="ml-auto text-[11px] font-normal text-slate-500" data-testid="solon-checked-at">
                    Έλεγχος: {caseData.solon_checked_at ? new Date(caseData.solon_checked_at).toLocaleString("el-GR") : "—"}
                    {caseData.solon_publications?.length ? ` · Αποφάσεις: ${caseData.solon_publications.length}` : ""}
                    {caseData.solon_pretrial_orders?.length ? ` · Διατάξεις Προδικασίας: ${caseData.solon_pretrial_orders.length}` : ""}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SolonTabsView tabs={caseData.solon_tabs} testPrefix="case-solon-tab" />
              </CardContent>
            </Card>
          )}

          {/* Parties */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Διάδικοι
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[11px] text-slate-500 bg-slate-50 border rounded px-2 py-1" data-testid="party-anon-hint">Τα στοιχεία διαδίκων αποθηκεύονται μόνο ως ψευδώνυμα «ΔΙΑΔ-…» (πλήρης κατακερματισμός HMAC-SHA256 κατά την καταχώριση — ισχύει για όλους τους χρήστες και τον διαχειριστή). Ό,τι πληκτρολογήσετε εδώ κατακερματίζεται με την αποθήκευση.</p>
              <div>
                <Label className="text-slate-500">Ενάγοντες / Αιτούντες</Label>
                {editing ? (
                  <Textarea
                    value={editForm.plaintiffs || ""}
                    onChange={(e) => setEditForm({...editForm, plaintiffs: e.target.value})}
                    placeholder="Στοιχεία εναγόντων"
                    rows={3}
                  />
                ) : (
                  <p className="font-medium whitespace-pre-wrap">{p(caseData.plaintiffs) || "-"}</p>
                )}
              </div>
              <div>
                <Label className="text-slate-500">Εναγόμενοι</Label>
                {editing ? (
                  <Textarea
                    value={editForm.defendants || ""}
                    onChange={(e) => setEditForm({...editForm, defendants: e.target.value})}
                    placeholder="Στοιχεία εναγομένων"
                    rows={3}
                  />
                ) : (
                  <p className="font-medium whitespace-pre-wrap">{p(caseData.defendants) || "-"}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Party Filings - Καταχώρηση Κατάθεσης */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-indigo-600" />
                  Καταχώρηση Κατάθεσης ανά Διάδικο
                </CardTitle>
                <Button size="sm" onClick={() => setShowFilingForm(true)} data-testid="add-filing-btn">
                  <Plus className="w-3 h-3 mr-1" /> Νέα Κατάθεση
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showFilingForm && (
                <div className="border rounded-lg p-3 mb-3 bg-slate-50 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Διάδικος</Label>
                      <Input value={filingForm.party_name} placeholder="Ονοματεπώνυμο..."
                        onChange={e => setFilingForm({...filingForm, party_name: e.target.value})}
                        data-testid="filing-party-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Ιδιότητα</Label>
                      <Select value={filingForm.party_type} onValueChange={v => setFilingForm({...filingForm, party_type: v})}>
                        <SelectTrigger data-testid="filing-party-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="plaintiff">Ενάγων</SelectItem>
                          <SelectItem value="defendant">Εναγόμενος</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">Τύπος</Label>
                      <Select value={filingForm.filing_type} onValueChange={v => setFilingForm({...filingForm, filing_type: v})}>
                        <SelectTrigger data-testid="filing-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="proposals">Προτάσεις</SelectItem>
                          <SelectItem value="addition_counter">Προσθήκη/Αντίκρουση</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Ημερομηνία</Label>
                      <DateInput value={filingForm.filing_date}
                        onChange={e => setFilingForm({...filingForm, filing_date: e.target.value})}
                        data-testid="filing-date" />
                    </div>
                    <div>
                      <Label className="text-xs">Ώρα</Label>
                      <Input type="time" value={filingForm.filing_time}
                        onChange={e => setFilingForm({...filingForm, filing_time: e.target.value})}
                        data-testid="filing-time" />
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <div>
                        <Label className="text-xs">Φάκελοι</Label>
                        <Input type="number" min={0} value={filingForm.folders_count}
                          onChange={e => setFilingForm({...filingForm, folders_count: parseInt(e.target.value)||0})}
                          data-testid="filing-folders" />
                      </div>
                      <div>
                        <Label className="text-xs">BOX</Label>
                        <Input type="number" min={0} value={filingForm.box_count}
                          onChange={e => setFilingForm({...filingForm, box_count: parseInt(e.target.value)||0})}
                          data-testid="filing-box" />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setShowFilingForm(false)}>Ακύρωση</Button>
                    <Button size="sm" onClick={handleAddFiling} data-testid="save-filing-btn">Αποθήκευση</Button>
                  </div>
                </div>
              )}
              {(caseData?.party_filings || []).length === 0 && !showFilingForm ? (
                <p className="text-sm text-slate-400">Δεν υπάρχουν καταχωρήσεις</p>
              ) : (
                <div className="space-y-2">
                  {(caseData?.party_filings || []).map(f => (
                    <div key={f.id} className="flex items-center justify-between p-2 border rounded-lg bg-white">
                      <div className="flex items-center gap-3">
                        <Badge variant={f.party_type === "plaintiff" ? "default" : "secondary"} className="text-xs">
                          {f.party_type === "plaintiff" ? "Ενάγων" : "Εναγόμενος"}
                        </Badge>
                        <span className="font-medium text-sm">{f.party_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {f.filing_type === "proposals" ? "Προτάσεις" : "Προσθήκη/Αντίκρουση"}
                        </Badge>
                        {f.filing_date && <span className="text-xs text-slate-500">{new Date(f.filing_date).toLocaleDateString("el-GR")} {f.filing_time || ""}</span>}
                        {(f.folders_count > 0 || f.box_count > 0) && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <FolderOpen className="w-3 h-3" />{f.folders_count}
                            <Package className="w-3 h-3 ml-1" />{f.box_count}
                          </span>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteFiling(f.id)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Σημειώσεις</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <Textarea
                  value={editForm.notes || ""}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Προσθέστε σημειώσεις..."
                  rows={4}
                />
              ) : (
                <p className="text-slate-600 whitespace-pre-wrap">
                  {caseData.notes || "Δεν υπάρχουν σημειώσεις"}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Service Reports — Εκθέσεις Επίδοσης ανά εναγόμενο */}
          <Card data-testid="service-reports-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Εκθέσεις Επίδοσης</CardTitle>
                <CardDescription>Καταχώρηση εκθέσεων επίδοσης ανά εναγόμενο/διάδικο, με τον υπεύθυνο δικαστικό επιμελητή.</CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowServiceReportForm(v => !v)} data-testid="toggle-service-report-form">
                {showServiceReportForm ? "Άκυρο" : "+ Προσθήκη"}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {showServiceReportForm && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 bg-slate-50 rounded-md border">
                  <div className="md:col-span-2">
                    <Label className="text-xs">Εναγόμενος / Διάδικος</Label>
                    {caseData?.defendants ? (
                      <Select
                        value={serviceReportForm.defendant_name}
                        onValueChange={v => setServiceReportForm({...serviceReportForm, defendant_name: v})}
                      >
                        <SelectTrigger data-testid="sr-defendant-select"><SelectValue placeholder="Επιλέξτε..." /></SelectTrigger>
                        <SelectContent>
                          {String(caseData.defendants).split(/[,\n]/).map(s => s.trim()).filter(Boolean).map((d) => (
                            <SelectItem key={`def-${d}`} value={d}>{p(d)}</SelectItem>
                          ))}
                          <SelectItem value="__custom__">— Άλλο (πληκτρολόγηση) —</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : null}
                    {(serviceReportForm.defendant_name === "__custom__" || !caseData?.defendants) && (
                      <Input
                        className="mt-2"
                        placeholder="Όνομα διαδίκου"
                        value={serviceReportForm.defendant_name === "__custom__" ? "" : serviceReportForm.defendant_name}
                        onChange={e => setServiceReportForm({...serviceReportForm, defendant_name: e.target.value})}
                        data-testid="sr-defendant-input"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Δικαστικός Επιμελητής</Label>
                    <Input
                      value={serviceReportForm.bailiff_name}
                      onChange={e => setServiceReportForm({...serviceReportForm, bailiff_name: e.target.value})}
                      placeholder="Ονοματεπώνυμο"
                      data-testid="sr-bailiff-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Αρ. Έκθεσης</Label>
                    <Input
                      value={serviceReportForm.report_number}
                      onChange={e => setServiceReportForm({...serviceReportForm, report_number: e.target.value})}
                      data-testid="sr-report-number"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Ημερομηνία</Label>
                    <DateInput
                      
                      value={serviceReportForm.report_date || ""}
                      onChange={e => setServiceReportForm({...serviceReportForm, report_date: e.target.value})}
                      data-testid="sr-report-date"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs">Σημειώσεις</Label>
                    <Input
                      value={serviceReportForm.notes}
                      onChange={e => setServiceReportForm({...serviceReportForm, notes: e.target.value})}
                      placeholder="Προαιρετικά"
                      data-testid="sr-notes"
                    />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button onClick={handleAddServiceReport} className="w-full" data-testid="sr-save-btn">
                      Καταχώρηση
                    </Button>
                  </div>
                </div>
              )}

              {(caseData?.service_reports || []).length === 0 && !showServiceReportForm ? (
                <p className="text-sm text-slate-400 text-center py-4">Δεν έχουν καταχωρηθεί εκθέσεις επίδοσης.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="p-2 text-left">Εναγόμενος / Διάδικος</th>
                        <th className="p-2 text-left">Δικ. Επιμελητής</th>
                        <th className="p-2 text-left">Αρ. Έκθεσης</th>
                        <th className="p-2 text-center">Ημερομηνία</th>
                        <th className="p-2 text-left">Σημειώσεις</th>
                        <th className="p-2 text-center w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(caseData?.service_reports || []).map(r => (
                        <tr key={r.id} className="border-b last:border-0" data-testid={`service-report-${r.id}`}>
                          <td className="p-2 font-medium">{p(r.defendant_name)}</td>
                          <td className="p-2">{r.bailiff_name || "—"}</td>
                          <td className="p-2 font-mono">{r.report_number || "—"}</td>
                          <td className="p-2 text-center font-mono text-xs">
                            {r.report_date ? formatDate(r.report_date).split(",")[0] : "—"}
                          </td>
                          <td className="p-2 text-slate-500 text-xs">
                            <div>{r.notes || "—"}</div>
                            {r.created_by_name && (
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Καταχ.: {r.created_by_name}{r.created_by_role ? ` (${r.created_by_role})` : ""}
                                {r.created_at && ` · ${formatDate(r.created_at).split(",")[0]}`}
                              </div>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-600 hover:text-red-800"
                              onClick={() => handleDeleteServiceReport(r.id)}
                              data-testid={`sr-delete-${r.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Deadlines Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Προθεσμίες (Άρθρα 237 & 269)
                  </CardTitle>
                  <CardDescription>
                    Ν.5221/2025 & Ν.5282/2026 - ΚΠολΔ
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportSummary}
                    data-testid="export-summary-btn">
                    <Download className="w-4 h-4 mr-1" />
                    Σημείωμα
                  </Button>
                  <Button variant="outline" size="sm" onClick={handlePrintDeadlines}
                    disabled={!caseData.deadlines?.length}
                    data-testid="print-deadlines-btn">
                    <Printer className="w-4 h-4 mr-1" />
                    Εκτύπωση
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="deadline-timeline">
                {caseData.deadlines?.map((deadline, index) => {
                  const status = getDeadlineStatus(deadline);
                  const StatusIcon = status.icon;
                  
                  return (
                    <div 
                      key={deadline.type}
                      className={`deadline-timeline-item ${
                        deadline.status === "completed" ? "completed" : 
                        status.class === "badge-urgent" ? "overdue" : ""
                      }`}
                      data-testid={`deadline-${deadline.type}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm">{deadline.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{deadline.description}</p>
                          <p className="text-sm font-mono mt-1">
                            {deadline.deadline_start ? (
                              <>
                                <span className="text-slate-500">{formatDate(deadline.deadline_start).split(",")[0]}</span>
                                <span className="text-slate-400 mx-1.5">→</span>
                                <span>{formatDate(deadline.deadline_date).split(",")[0]}</span>
                                <span className="ml-2 text-[10px] text-slate-400">({deadline.days_total} ημ.)</span>
                              </>
                            ) : (
                              formatDate(deadline.deadline_date).split(",")[0]
                            )}
                          </p>
                          {deadline.completed_by_name && (
                            <p className="text-[10px] text-emerald-700 mt-0.5">
                              Ολοκληρώθηκε από: {deadline.completed_by_name}
                              {deadline.completed_by_role ? ` (${deadline.completed_by_role})` : ""}
                              {deadline.completed_at && ` · ${formatDate(deadline.completed_at).split(",")[0]}`}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={status.class}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                          {deadline.status !== "completed" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-7"
                              onClick={() => updateDeadlineStatus(deadline.type, "completed")}
                              data-testid={`complete-deadline-${deadline.type}`}
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Ολοκλήρωση
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Related Cases (Ένδικα Βοηθήματα ↔ Κύρια Αγωγή) */}
          <Card data-testid="related-cases-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-amber-600" />
                Σχετικά Δικόγραφα
              </CardTitle>
              <CardDescription>
                Σύνδεση ένδικων βοηθημάτων (Πρόσθετη Παρέμβαση, Ανακοίνωση Δίκης, Προσεπίκληση κλπ) με την κύρια αγωγή.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* If this case is linked to a main case */}
              {caseData?.related_main_gak && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="text-xs text-amber-700 font-medium mb-1">
                    🔗 Αυτή η υπόθεση συνδέεται με κύρια αγωγή:
                  </div>
                  {related.main_case ? (
                    <Link
                      to={`/cases/${related.main_case.id}`}
                      className="block hover:bg-amber-100 -mx-2 px-2 py-1 rounded"
                      data-testid="link-to-main-case"
                    >
                      <div className="font-bold text-slate-900">
                        ΓΑΚ {related.main_case.gak_number}/{related.main_case.gak_year}
                        {" · "}ΕΑΚ {related.main_case.eak_number || "—"}/{related.main_case.gak_year}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {related.main_case.case_type || "—"} · {related.main_case.subject || "—"}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {p(related.main_case.plaintiffs)} κατά {p(related.main_case.defendants)}
                      </div>
                    </Link>
                  ) : (
                    <div className="text-sm text-slate-600">
                      ΓΑΚ {caseData.related_main_gak}/{caseData.related_main_year || "—"}
                      <span className="ml-2 text-xs text-red-600">(δεν βρέθηκε στη βάση)</span>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="mt-2 text-red-600"
                    onClick={handleUnlinkMain}
                    data-testid="unlink-main-case-btn"
                  >
                    <X className="w-3 h-3 mr-1" /> Αποσύνδεση
                  </Button>
                </div>
              )}

              {/* If NOT linked, show input to link */}
              {!caseData?.related_main_gak && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">
                    Συσχέτιση με Κύρια Αγωγή (εισάγετε ΓΑΚ κύριας αγωγής):
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="ΓΑΚ"
                      value={linkGakInput}
                      onChange={(e) => setLinkGakInput(e.target.value)}
                      className="w-32"
                      data-testid="link-main-gak-input"
                    />
                    <Input
                      placeholder={`Έτος (${caseData?.gak_year || ""})`}
                      value={linkYearInput}
                      onChange={(e) => setLinkYearInput(e.target.value)}
                      className="w-28"
                      data-testid="link-main-year-input"
                    />
                    <Button
                      onClick={handleLinkMain}
                      disabled={linkingMain || !linkGakInput.trim()}
                      data-testid="link-main-btn"
                    >
                      {linkingMain ? <Loader2 className="w-4 h-4 animate-spin" /> : "Σύνδεση"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Children: ένδικα βοηθήματα που στρέφονται κατά αυτής της υπόθεσης */}
              {related.children_count > 0 && (
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center justify-between gap-2 flex-wrap">
                    <span data-testid="related-children-title">
                      Παρεπόμενα Δικόγραφα κατά αυτής της υπόθεσης ({related.children_count})
                      {related.children_count > related.children.length && <span className="text-xs font-normal text-slate-500"> · εμφανίζονται τα {related.children.length}</span>}
                      {related.ignored_count > 0 && <span className="text-xs font-normal text-slate-400"> · {related.ignored_count} αγνοημένα</span>}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {related.children.map((c) => (
                      <Link
                        key={c.id}
                        to={`/cases/${c.id}`}
                        className="block p-2 border border-slate-200 rounded hover:bg-slate-50"
                        data-testid={`related-child-${c.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-slate-900">
                              {c.case_type || "Ένδικο Βοήθημα"} — ΓΑΚ {c.gak_number}/{c.gak_year} · ΕΑΚ {c.eak_number || "—"}/{c.gak_year}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {p(c.plaintiffs)} κατά {p(c.defendants)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {c.hearing_date && (
                              <Badge variant="outline" className="text-xs">
                                Δικάσιμος: {c.hearing_date.split("-").reverse().join("/")}
                              </Badge>
                            )}
                            <button type="button" className="text-[11px] text-slate-400 hover:text-red-600 underline"
                              title="Αγνόηση: δεν θα εμφανίζεται ως παρεπόμενο αυτής της υπόθεσης"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); ignoreChild(c.id, true); }}
                              data-testid={`related-child-ignore-${c.id}`}>Αγνόηση</button>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  {related.children_count > related.children.length && (
                    <p className="text-[11px] text-slate-500 mt-2" data-testid="related-children-truncated">
                      Για λόγους απόδοσης εμφανίζονται έως 5 παρεπόμενα δικόγραφα· τα υπόλοιπα {related.children_count - related.children.length} είναι διαθέσιμα από τη Λίστα Υποθέσεων (αναζήτηση ΓΑΚ {caseData.gak_number}/{caseData.gak_year}).
                    </p>
                  )}
                  {related.ignored_count > 0 && (
                    <button type="button" className="text-[11px] text-slate-500 underline mt-1" onClick={restoreIgnored} data-testid="related-children-restore-btn">
                      Επαναφορά αγνοημένων ({related.ignored_count})
                    </button>
                  )}
                </div>
              )}

              {related.children_count === 0 && related.ignored_count > 0 && (
                <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="related-children-all-ignored">
                  Όλα τα παρεπόμενα δικόγραφα ({related.ignored_count}) έχουν αγνοηθεί.
                  <button type="button" className="underline text-slate-600" onClick={restoreIgnored} data-testid="related-children-restore-btn">Επαναφορά</button>
                </div>
              )}

              {!caseData?.related_main_gak && related.children_count === 0 && related.ignored_count === 0 && (
                <div className="text-xs text-slate-500 italic">
                  Δεν υπάρχουν συνδεδεμένα δικόγραφα.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Additional Info */}
          {(caseData.last_hearing_date || caseData.publication_details || caseData.withdrawal_indicator) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Επιπλέον Πληροφορίες</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {caseData.last_hearing_date && (
                  <div>
                    <Label className="text-slate-500">Τελευταία Δικάσιμος</Label>
                    <p className="font-medium">{caseData.last_hearing_date}</p>
                  </div>
                )}
                {caseData.publication_details && (
                  <div>
                    <Label className="text-slate-500">Στοιχεία Δημοσίευσης</Label>
                    <p className="font-medium">{caseData.publication_details}</p>
                  </div>
                )}
                {caseData.withdrawal_indicator && (
                  <div>
                    <Label className="text-slate-500">Παραίτηση</Label>
                    <p className="font-medium text-amber-600">
                      {caseData.withdrawal_type || "Ναι"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Υπόθεσης</DialogTitle>
            <DialogDescription>
              Είστε σίγουροι ότι θέλετε να διαγράψετε την υπόθεση ΓΑΚ {caseData.gak_number}/{caseData.gak_year} · ΕΑΚ {caseData.eak_number || "—"}/{caseData.gak_year};
              Η ενέργεια δεν μπορεί να αναιρεθεί.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>
              Ακύρωση
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Διαγραφή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Πράξης Ορισμού στον δικαστή */}
      <Dialog open={emailActOpen} onOpenChange={setEmailActOpen}>
        <DialogContent className="max-w-xl" data-testid="email-act-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Αποστολή Πράξης Ορισμού στον δικαστή
            </DialogTitle>
            <DialogDescription>
              Παράγεται η Πράξη Ορισμού και επισυνάπτεται σε email προς τον ορισμένο δικαστή, ώστε να ενημερωθεί και να προγραμματίσει τον προδικαστικό έλεγχο (άρθρο 237 ΚΠολΔ).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {emailActJudges.length > 0 && (
              <div>
                <Label className="text-xs">Γρήγορη επιλογή Δικαστή (αυτο-συμπληρώνει email)</Label>
                <Select
                  value=""
                  onValueChange={(val) => {
                    const j = emailActJudges.find(x => x.id === val);
                    if (j && j.email) {
                      setEmailActForm({ ...emailActForm, to_email: j.email });
                      toast.success(`Επιλέχθηκε: ${j.full_name} → ${j.email}`);
                    } else if (j) {
                      toast.warning(`Ο ${j.full_name} δεν έχει email στη βάση. Πληκτρολογήστε χειροκίνητα.`);
                    }
                  }}
                >
                  <SelectTrigger className="text-xs h-9" data-testid="email-act-judge-select">
                    <SelectValue placeholder="Επιλέξτε από τη λίστα..." />
                  </SelectTrigger>
                  <SelectContent>
                    {emailActJudges.map(j => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.full_name}{j.grade ? ` (${j.grade})` : ""}{j.email ? ` — ${j.email}` : " — [χωρίς email]"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Email Δικαστή (επεξεργάσιμο)</Label>
              <Input
                type="email"
                value={emailActForm.to_email}
                onChange={(e) => setEmailActForm({ ...emailActForm, to_email: e.target.value })}
                placeholder="judge@example.com"
                data-testid="email-act-to-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Μπορείτε να γράψετε <b>οποιαδήποτε</b> διεύθυνση χειροκίνητα — δεν χρειάζεται να υπάρχει στη βάση.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Αρ. Πράξης</Label>
                <Input
                  value={emailActForm.act_number}
                  onChange={(e) => setEmailActForm({ ...emailActForm, act_number: e.target.value })}
                  placeholder="π.χ. 1"
                  data-testid="email-act-num-input"
                />
              </div>
              <div>
                <Label className="text-xs">Format Συνημμένου</Label>
                <Select
                  value={emailActForm.format}
                  onValueChange={(v) => setEmailActForm({ ...emailActForm, format: v })}
                >
                  <SelectTrigger data-testid="email-act-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="docx">DOCX (Word)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="odt">ODT (OpenDocument)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Θέμα Email (προαιρετικό — αν είναι κενό χρησιμοποιείται το τυποποιημένο)</Label>
              <Input
                value={emailActForm.subject}
                onChange={(e) => setEmailActForm({ ...emailActForm, subject: e.target.value })}
                placeholder="Αυτόματο: Πράξη Ορισμού N/YYYY — ΓΑΚ X/Y"
                data-testid="email-act-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs">Σώμα Email (προαιρετικό — αν είναι κενό χρησιμοποιείται το τυποποιημένο)</Label>
              <Textarea
                rows={5}
                value={emailActForm.body}
                onChange={(e) => setEmailActForm({ ...emailActForm, body: e.target.value })}
                placeholder="Αυτόματο: Τυποποιημένη επιστολή με αναφορά σε άρθρο 237 ΚΠολΔ και 30ήμερη προθεσμία προδικαστικού."
                className="text-xs"
                data-testid="email-act-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailActOpen(false)} disabled={emailActSending}>
              Ακύρωση
            </Button>
            <Button onClick={handleSendActEmail} disabled={emailActSending} data-testid="email-act-send-btn">
              {emailActSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Αποστολή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Πράξης Ανάθεσης Εισηγητή */}
      <Dialog open={emailRappOpen} onOpenChange={setEmailRappOpen}>
        <DialogContent className="max-w-xl" data-testid="email-rapp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Αποστολή Πράξης Ανάθεσης Εισηγητή
            </DialogTitle>
            <DialogDescription>
              Παράγεται η Πράξη Ανάθεσης σε Εισηγητή (Πολυμελές) και αποστέλλεται με attachment, ώστε ο Εισηγητής να ξεκινήσει τον προδικαστικό έλεγχο. Προαιρετικά συμπεριλαμβάνεται και η Πράξη Σύνθεσης για context.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email Εισηγητή / Προέδρου Σύνθεσης</Label>
              <Input
                type="email"
                value={emailRappForm.to_email}
                onChange={(e) => setEmailRappForm({ ...emailRappForm, to_email: e.target.value })}
                placeholder="rapporteur@example.com"
                data-testid="email-rapp-to-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Αρ. Πράξης Ανάθεσης</Label>
                <Input
                  value={emailRappForm.act_number}
                  onChange={(e) => setEmailRappForm({ ...emailRappForm, act_number: e.target.value })}
                  placeholder="π.χ. 6"
                  data-testid="email-rapp-num-input"
                />
              </div>
              <div>
                <Label className="text-xs">Format Συνημμένων</Label>
                <Select
                  value={emailRappForm.format}
                  onValueChange={(v) => setEmailRappForm({ ...emailRappForm, format: v })}
                >
                  <SelectTrigger data-testid="email-rapp-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="docx">DOCX (Word)</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="odt">ODT (OpenDocument)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include-synthesis-cb"
                checked={!!emailRappForm.include_synthesis}
                onChange={(e) => setEmailRappForm({ ...emailRappForm, include_synthesis: e.target.checked })}
                className="w-4 h-4"
                data-testid="email-rapp-include-synthesis"
              />
              <Label htmlFor="include-synthesis-cb" className="text-xs cursor-pointer">
                Επισύναψη και της Πράξης Σύνθεσης ({caseData.assignment_act_number ? `${caseData.assignment_act_number}/${caseData.assignment_act_year || caseData.gak_year}` : "—"})
              </Label>
            </div>
            <div>
              <Label className="text-xs">Θέμα Email (προαιρετικό)</Label>
              <Input
                value={emailRappForm.subject}
                onChange={(e) => setEmailRappForm({ ...emailRappForm, subject: e.target.value })}
                placeholder="Αυτόματο"
                data-testid="email-rapp-subject-input"
              />
            </div>
            <div>
              <Label className="text-xs">Σώμα Email (προαιρετικό)</Label>
              <Textarea
                rows={5}
                value={emailRappForm.body}
                onChange={(e) => setEmailRappForm({ ...emailRappForm, body: e.target.value })}
                placeholder="Αυτόματο: Τυποποιημένη επιστολή"
                className="text-xs"
                data-testid="email-rapp-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailRappOpen(false)} disabled={emailRappSending}>
              Ακύρωση
            </Button>
            <Button onClick={handleSendRappEmail} disabled={emailRappSending} className="bg-amber-600 hover:bg-amber-700" data-testid="email-rapp-send-btn">
              {emailRappSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
              Αποστολή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Judge Picker Dialog */}
      <ActIssueDialog open={actIssue.open} onOpenChange={(o) => setActIssue(a => ({ ...a, open: o }))}
        title={actIssue.kind === "rapporteur" ? "Πράξη Ορισμού Εισηγητή" : (caseData?.court_type === "Πολυμελές" ? "Πράξη Ορισμού Σύνθεσης" : "Πράξη Ορισμού Δικαστή")}
        description={`ΓΑΚ ${caseData?.gak_number || ""}/${caseData?.gak_year || ""} · ${caseData?.court_type || ""}`}
        year={caseData?.gak_year}
        defaultActNumber={actIssue.kind === "rapporteur" ? (caseData?.rapporteur_act_number || "") : (caseData?.assignment_act_number || "")}
        suggestActNumber={actIssue.kind === "rapporteur" ? undefined : suggestActNumber}
        defaultIssueDate={((actIssue.kind === "rapporteur" ? caseData?.rapporteur_act_issue_date : caseData?.assignment_act_issue_date) || "").slice(0, 10) || undefined}
        initialFormat={actIssue.format} onConfirm={confirmActIssue} testPrefix={actIssue.kind === "rapporteur" ? "cd-rapp-act" : "cd-act"}
        showNoteOption
        noteHint={actIssue.kind !== "rapporteur" && caseData?.court_type === "Πολυμελές" ? "ισχύει για Πράξεις Ορισμού Εισηγητή για προδικαστικό έλεγχο, όχι για Πράξη Σύνθεσης" : ""} />
      <JudgeAssignDialog open={showJudgePicker} onOpenChange={setShowJudgePicker} caseData={caseData} judges={pretrialJudges} user={user}
        onDone={() => { fetchCase(); fetchGeneratedDocs(); }} />
      <CompositionAssignDialog open={showCompositionPicker} onOpenChange={setShowCompositionPicker} caseData={caseData} user={user}
        onDone={() => { fetchCase(); fetchGeneratedDocs(); }} />
    </div>
  );
};

export default CaseDetail;
